import { useState, useRef } from 'react';
import { ChevronRight, User, Palette, LogOut } from 'lucide-react';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useAuth } from '@/context/AuthContext';
import clsx from 'clsx';

type UserSettingsTab = 'user' | 'appearance';

interface UserMenuProps {
  onOpenUserSettings?: (tab?: UserSettingsTab) => void;
  onLogout?: () => void;
}

export function UserMenu({ onOpenUserSettings, onLogout }: UserMenuProps) {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { user, signOut } = useAuth();

  const avatarUrl = user?.user_metadata?.avatar_url;
  const fullName = user?.user_metadata?.full_name || 'Utente';
  const email = user?.email || '';
  const initials = fullName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();

  useClickOutside([userMenuRef], () => setIsUserMenuOpen(false), isUserMenuOpen);

  return (
    <div className="px-3 py-2 border-t border-border-primary bg-surface-tertiary/20">
      <div className="relative" ref={userMenuRef}>
        <button
          onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
          className={clsx(
            'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md transition-colors',
            isUserMenuOpen ? 'bg-surface-hover' : 'hover:bg-surface-hover'
          )}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt={fullName} className="w-8 h-8 rounded-full ring-2 ring-transparent hover:ring-accent-violet/30 transition-all" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-emerald to-accent-blue flex items-center justify-center ring-2 ring-transparent hover:ring-accent-violet/30 transition-all">
              <span className="text-[11px] font-bold text-white">{initials}</span>
            </div>
          )}
          <div className="flex-1 min-w-0 text-left">
            <div className="text-[12px] font-medium text-text-primary truncate">{fullName}</div>
            <div className="text-[10px] text-text-quaternary truncate">{email}</div>
          </div>
          <ChevronRight
            className={clsx(
              'w-3.5 h-3.5 text-text-quaternary transition-transform',
              isUserMenuOpen && 'rotate-90'
            )}
          />
        </button>

        {/* User Dropdown Menu */}
        {isUserMenuOpen && (
          <div className="absolute bottom-full left-0 right-0 mb-1 bg-surface-secondary border border-border-primary rounded-lg shadow-xl overflow-hidden animate-scale-in origin-bottom z-50">
            <div className="p-1">
              <button
                onClick={() => {
                  onOpenUserSettings?.('user');
                  setIsUserMenuOpen(false);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[11px] text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
              >
                <User className="w-4 h-4" />
                <span>Profilo</span>
              </button>
              <button
                onClick={() => {
                  onOpenUserSettings?.('appearance');
                  setIsUserMenuOpen(false);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[11px] text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
              >
                <Palette className="w-4 h-4" />
                <span>Aspetto</span>
                <div className="flex-1" />
                <div className="flex items-center gap-0.5">
                  <kbd className="kbd">⌘</kbd>
                  <kbd className="kbd">,</kbd>
                </div>
              </button>
            </div>
            <div className="border-t border-border-primary p-1">
              <button
                onClick={async () => {
                  await signOut();
                  onLogout?.();
                  setIsUserMenuOpen(false);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[11px] text-accent-rose hover:bg-accent-rose/10 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Esci</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
